import { useApp } from "../../../../services/app-context";
import { getLabel } from "../../utils/labels";

interface CertificationContinuePromptProps {
  completedAttemptNumber: number;
  minimumAttempts: number;
  onStartLater: () => void;
  onBeginNext: () => void;
}

export function CertificationContinuePrompt({
  completedAttemptNumber,
  minimumAttempts,
  onStartLater,
  onBeginNext,
}: CertificationContinuePromptProps) {
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels ?? {};

  const attemptSingular = getLabel("attempt.singular", labels);
  const attemptPlural = getLabel("attempt.plural", labels);
  const productName = getLabel("certificationName.singular", labels);
  const attemptsReviewedText = getLabel("attemptsReviewedText", labels);
  const questionPlural = getLabel("question.plural", labels);
  const nextAttemptNumber = completedAttemptNumber + 1;

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center px-4">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-[588px] flex flex-col gap-5">
        {/* Pill badge */}
        <div className="inline-flex">
          <span className="bg-green-50 text-green-700 text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
            {attemptSingular} {completedAttemptNumber} Submitted
          </span>
        </div>

        {/* Heading + body */}
        <div className="flex flex-col gap-5">
          <p className="text-xl font-medium text-text-primary leading-normal">
            {attemptSingular} {nextAttemptNumber} is required
          </p>
          <p className="text-sm text-text2 leading-snug">
            This {productName} requires {minimumAttempts}{" "}
            {minimumAttempts === 1 ? attemptSingular.toLowerCase() : attemptPlural.toLowerCase()}.
            Your next {attemptSingular.toLowerCase()} will use the same set of scenarios and {questionPlural.toLowerCase()}.
          </p>
        </div>

        {/* Info box */}
        <div className="bg-gray-100 border border-gray-200 rounded-xl px-5 py-4">
          <p className="text-xs text-gray-700 leading-snug">
            {minimumAttempts === 2
              ? `Your results from both ${attemptPlural.toLowerCase()} ${attemptsReviewedText}`
              : `Your results from all ${minimumAttempts} ${attemptPlural.toLowerCase()} ${attemptsReviewedText}`}{" "}
            There is no minimum waiting period before starting.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-5 items-center">
          <button
            onClick={onStartLater}
            className="flex-1 py-2 px-4 rounded-full border border-primary text-primary text-base font-pp-regular hover:bg-primary/5 transition-colors"
          >
            Start Later
          </button>
          <button
            onClick={onBeginNext}
            className="flex-1 py-2 px-4 rounded-full bg-primary text-white text-base font-pp-regular hover:bg-primary/90 transition-colors"
          >
            Begin {attemptSingular} {nextAttemptNumber}
          </button>
        </div>
      </div>
    </div>
  );
}
